import React, { useEffect, useState } from 'react';
import { Box, Button, MenuItem, TextField, useTheme } from '@mui/material';
import { FaFilter } from 'react-icons/fa';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { Grid } from "@material-ui/core";
import useGlobalContext from "../../Hooks/useGlobalContext";
import getInitialDataFilter from "./initialDataFilter";
import { IFilter } from "./types";

const primaryColor = '#1c2e4a';

interface INFEPageFilterProps {
    dataInicioCompetencia?: string;
    dataFimCompetencia?: string;
}

const NFEPageFilter: React.FC<INFEPageFilterProps> = ({ dataInicioCompetencia, dataFimCompetencia }) => {
    const theme = useTheme(); // Adiciona suporte ao tema

    const { filters, setFilters, showFilters, setShowFilters } = useGlobalContext();

    const [initialDate, setInitialDate] = useState<Dayjs | null>(
        dataInicioCompetencia ? dayjs(dataInicioCompetencia) : dayjs(filters?.startDate || undefined)
    );
    const [selectedEndDate, setSelectedEndDate] = useState<Dayjs | null>(
        dataFimCompetencia ? dayjs(dataFimCompetencia) : dayjs(filters?.endDate || undefined)
    );

    const [num_doc, setNumDoc] = useState<string>(filters?.num_doc || '');
    const [chv_nfe, setChvNfe] = useState<string>(filters?.chv_nfe || '');

    const [selectedButton, setSelectedButton] = useState('');
    const [loading, setLoading] = useState(false);

    const runFilters = () => {
        if (setFilters) {
            setFilters((prev) => ({
                ...prev,
                startDate: initialDate?.format('YYYY-MM-DD') || '',
                endDate: selectedEndDate?.format('YYYY-MM-DD') || '',
                establishment: '',
                num_doc: num_doc || '',
                chv_nfe: chv_nfe || '',
            }));
        }
    };

    const clearFilters = () => {
        if (setFilters) {
            const initialFilters = getInitialDataFilter();
            
            // Se temos competência selecionada, usar suas datas ao invés das padrão
            if (dataInicioCompetencia && dataFimCompetencia) {
                initialFilters.startDate = dataInicioCompetencia;
                initialFilters.endDate = dataFimCompetencia;
            }
            
            setFilters(initialFilters);
        }
        setNumDoc('');
        setChvNfe('');
        // Manter as datas da competência quando limpar filtros
        if (dataInicioCompetencia && dataFimCompetencia) {
            setInitialDate(dayjs(dataInicioCompetencia));
            setSelectedEndDate(dayjs(dataFimCompetencia));
        }
    };

    const isToday = (date: string) => {
        const today = dayjs();
        const givenDate = dayjs(date);
        return givenDate.isSame(today, 'day');
    };

    useEffect(() => {
        if (initialDate && selectedEndDate && isToday(selectedEndDate.format('YYYY-MM-DD'))) {
            const days = (selectedEndDate?.diff(initialDate, 'day')) ?? 0
            setSelectedButton((days + 1) + '');
        } else {
            setSelectedButton('0');
        }
    }, [initialDate, selectedEndDate]);

    useEffect(() => {
        // Só aplica filtros globais se não tiver competência selecionada
        if (!dataInicioCompetencia && !dataFimCompetencia) {
            if (filters?.startDate) {
                setInitialDate(dayjs(filters.startDate));
            }
            if (filters?.endDate) {
                setSelectedEndDate(dayjs(filters.endDate));
            }
        }
    }, [filters, dataInicioCompetencia, dataFimCompetencia]);

    // Ajustar datas automaticamente quando competência mudar (prioridade sobre filtros globais)
    useEffect(() => {
        if (dataInicioCompetencia && dataFimCompetencia) {
            setInitialDate(dayjs(dataInicioCompetencia));
            setSelectedEndDate(dayjs(dataFimCompetencia));
        }
    }, [dataInicioCompetencia, dataFimCompetencia]);

    const setDataPageFilter = (filtersParam: IFilter | undefined) => {
        if (filtersParam) {
            setInitialDate(dayjs(filtersParam.startDate || undefined));
            setSelectedEndDate(dayjs(filtersParam.endDate || undefined));
        }
    };

    return (
        <Grid item xs={12} md={12} lg={12}>
            <Box sx={{ backgroundColor: theme.palette.background.paper }} p={1}>
                <Grid item xs={12}>
                    <Button onClick={() => {
                        if (setShowFilters) {
                            setShowFilters((n: boolean) => !n);
                        }
                    }}>
                        <span style={{ marginRight: '10px' }}>
                            <FaFilter />
                        </span>
                        {showFilters ? 'Esconder Filtros' : 'Mostrar Filtros'}
                    </Button>
                </Grid>
                {showFilters && (
                    <Grid item xs={12} style={{ marginTop: 2 }} component="section">
                        <Grid container spacing={2} style={{ maxWidth: '100%' }}>
                            <Grid item xs={12} sm={6} md={3} lg={3}>
                                <DatePicker
                                    label="Data inicial"
                                    value={initialDate}
                                    disabled={loading}
                                    onChange={setInitialDate}
                                    views={['year', 'month', 'day']}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3} lg={3}>
                                <DatePicker
                                    label="Data final"
                                    value={selectedEndDate}
                                    disabled={loading}
                                    onChange={setSelectedEndDate}
                                    views={['year', 'month', 'day']}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={3} md={3} lg={3}>
                                <TextField
                                    label="NÚMERO DO DOCUMENTO"
                                    disabled={loading}
                                    variant="outlined"
                                    value={num_doc}
                                    onChange={e => setNumDoc(e.target.value)}
                                    onClick={() => setNumDoc('')}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={3} md={3} lg={3}>
                                <TextField
                                    label="CHAVE DA NFE"
                                    disabled={loading}
                                    variant="outlined"
                                    value={chv_nfe}
                                    onChange={e => setChvNfe(e.target.value)}
                                    onClick={() => setChvNfe('')}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={6} style={{ marginBottom: 2 }}>
                                <Button
                                    sx={{
                                        borderRadius: '0px',
                                        border: '1px solid',
                                        margin: '0',
                                        width: '100%'
                                    }}
                                    disabled={loading}
                                    onClick={clearFilters}
                                >
                                    Limpar filtros
                                </Button>
                            </Grid>
                            <Grid item xs={6} style={{ marginBottom: 2 }}>
                                <Button
                                    sx={{
                                        borderRadius: '0px',
                                        border: '1px solid #1c2e4a',
                                        margin: '0',
                                        backgroundColor: primaryColor,
                                        color: 'white',
                                        width: '100%'
                                    }}
                                    disabled={loading}
                                    onClick={runFilters}
                                >
                                    Filtrar
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                )}
            </Box>
        </Grid>
    );
};

export default NFEPageFilter;
